import {
  AiInsights_AiInsightListItem_Type as AiInsightListItem_Type,
  AiInsights_AiInsightListItem_EntityType as EntityType,
} from '@zendesk/zqa-services/autoqa'
import { UiTagTheme, type UiTagThemeType } from '@klausapp/ui-kit/types'
import { AutoQaSpotlightType } from '@/types/spotlight'
import { AutoQaCategoryType } from '@/types/category'

import { AutoValidationError } from '@/modules/settings/components/ai/types'
import type { DeconstructedOption } from '@/modules/conversations/utils/filters'
import i18n from '@/i18n'
import { type Category } from '@/modules/shared/Review/utils'

export const systemSpotlightMap = {
  [AiInsightListItem_Type.DEAD_AIR]: AutoQaSpotlightType.DeadAir,
  [AiInsightListItem_Type.RECORDING_DISCLOSURE]: AutoQaSpotlightType.RecordingDisclosure,
  [AiInsightListItem_Type.CHURN_RISK]: AutoQaSpotlightType.ChurnRisk,
  [AiInsightListItem_Type.EXTRA_MILE]: AutoQaSpotlightType.ExtraMile,
  [AiInsightListItem_Type.SENTIMENT]: AutoQaSpotlightType.Sentiment,
  [AiInsightListItem_Type.OUTLIER]: AutoQaSpotlightType.Outlier,
  [AiInsightListItem_Type.FOLLOW_UP]: AutoQaSpotlightType.FollowUp,
  [AiInsightListItem_Type.ESCALATION_REQUEST]: AutoQaSpotlightType.EscalationRequest,
  [AiInsightListItem_Type.SLA]: AutoQaSpotlightType.SLA,
  [AiInsightListItem_Type.BOT_REPETITION]: AutoQaSpotlightType.BotRepetition,
  [AiInsightListItem_Type.BOT_COMMUNICATION_EFFICIENCY]: AutoQaSpotlightType.BotCommunicationEfficiency,
}

export const systemCategoryMap = {
  [AiInsightListItem_Type.COMPREHENSION]: AutoQaCategoryType.Comprehension,
}

export const getUsageAppearanceMap = (): Record<EntityType, { id: string; label: string; theme: UiTagThemeType }> => ({
  [EntityType.CATEGORY]: {
    id: 'CATEGORY',
    label: i18n.t('settings.ai.usage.category.label'),
    theme: UiTagTheme.BrandLight,
  },
  [EntityType.SPOTLIGHT]: {
    id: 'SPOTLIGHT',
    label: i18n.t('settings.ai.usage.spotlight.label'),
    theme: UiTagTheme.YellowLight,
  },
})

export const includesHandledValidationError = (error: string) => {
  const errorsArray = error.split(';').map((error) => error.trim())
  const enumValues: string[] = Object.values(AutoValidationError)
  return errorsArray.some((error) => enumValues.includes(error.toLowerCase()))
}

export const mapFilterOptions = (options: DeconstructedOption[]) =>
  options.map(({ option, condition }) => ({
    option: option.name,
    condition: condition.name,
  }))

enum FeDefinedAiInsightListItem_Type {
  ISSUE_UNDERSTANDING = 'ISSUE_UNDERSTANDING',
}

export function translateSystemCategoryName(code: string) {
  if (!code) return ''

  const categoryNameKeys = {
    [AiInsightListItem_Type.COMPREHENSION]: i18n.t(
      'settings.workspaces.rating_categories.edit.auto_categories.issue_understanding.name',
    ),
    [AiInsightListItem_Type.EMPATHY]: i18n.t('settings.workspaces.rating_categories.edit.auto_categories.empathy.name'),
    [AiInsightListItem_Type.GREETING]: i18n.t(
      'settings.workspaces.rating_categories.edit.auto_categories.greeting.name',
    ),
    [AiInsightListItem_Type.CLOSING]: i18n.t('settings.workspaces.rating_categories.edit.auto_categories.closing.name'),
    [AiInsightListItem_Type.GRAMMAR]: i18n.t('settings.workspaces.rating_categories.edit.auto_categories.grammar.name'),
    [AiInsightListItem_Type.READABILITY]: i18n.t(
      'settings.workspaces.rating_categories.edit.auto_categories.readability.name',
    ),
    [AiInsightListItem_Type.TONE]: i18n.t('settings.workspaces.rating_categories.edit.auto_categories.tone.name'),
    [AiInsightListItem_Type.SOLUTION]: i18n.t(
      'settings.workspaces.rating_categories.edit.auto_categories.solution.name',
    ),
    [FeDefinedAiInsightListItem_Type.ISSUE_UNDERSTANDING]: i18n.t(
      'settings.workspaces.rating_categories.edit.auto_categories.issue_understanding.name',
    ),
  }

  const key = categoryNameKeys[code.toUpperCase()]
  return key ?? ''
}

export function translateSystemCategoryDescription(code: string) {
  if (!code) return ''

  const categoryDescKeys = {
    [AiInsightListItem_Type.COMPREHENSION]: i18n.t('settings.ai.categories.issue_understanding.description'),
    [AiInsightListItem_Type.EMPATHY]: i18n.t('settings.ai.categories.empathy.description'),
    [AiInsightListItem_Type.GREETING]: i18n.t('settings.ai.categories.greeting.description_v2'),
    [AiInsightListItem_Type.CLOSING]: i18n.t('settings.ai.categories.closing.description_v2'),
    [AiInsightListItem_Type.GRAMMAR]: i18n.t('settings.ai.categories.grammar.description'),
    [AiInsightListItem_Type.READABILITY]: i18n.t('settings.ai.categories.readability.description'),
    [AiInsightListItem_Type.TONE]: i18n.t('settings.ai.categories.tone.description'),
    [AiInsightListItem_Type.SOLUTION]: i18n.t('settings.ai.categories.solution.description'),
    [FeDefinedAiInsightListItem_Type.ISSUE_UNDERSTANDING]: i18n.t(
      'settings.ai.categories.issue_understanding.description',
    ),
  }

  const key = categoryDescKeys[code.toUpperCase()]
  return key ?? ''
}

export function translateSystemSpotlightName(code: string) {
  if (!code) return ''

  const spotlightNameKeys = {
    [AiInsightListItem_Type.DEAD_AIR]: i18n.t('settings.spotlight.deadair.title'),
    [AiInsightListItem_Type.RECORDING_DISCLOSURE]: i18n.t('settings.spotlight.recording_disclosure.title'),
    [AiInsightListItem_Type.CHURN_RISK]: i18n.t('prompt_library.prompt.spotlight.name.churn_risk'),
    [AiInsightListItem_Type.EXTRA_MILE]: i18n.t('settings.spotlight.extra_mile.title'),
    [AiInsightListItem_Type.SENTIMENT]: i18n.t('settings.spotlight.sentiment.title'),
    [AiInsightListItem_Type.OUTLIER]: i18n.t('settings.spotlight.outlier.title'),
    [AiInsightListItem_Type.FOLLOW_UP]: i18n.t('settings.spotlight.follow_up.title'),
    [AiInsightListItem_Type.ESCALATION_REQUEST]: i18n.t('settings.spotlight.escalation_request.title'),
    [AiInsightListItem_Type.SLA]: i18n.t('settings.spotlight.sla.title'),
    [AiInsightListItem_Type.BOT_REPETITION]: i18n.t('filter.option.label.bot_repetition'),
    [AiInsightListItem_Type.BOT_COMMUNICATION_EFFICIENCY]: i18n.t('filter.option.label.bot_communication_efficiency'),
  }

  const key = spotlightNameKeys[code.toUpperCase()]
  return key ?? ''
}

export function translateSystemSpotlightDescription(code: string) {
  if (!code) return ''

  const spotlightDescKeys = {
    [AiInsightListItem_Type.DEAD_AIR]: i18n.t('settings.ai.spotlights.dead_air.description'),
    [AiInsightListItem_Type.RECORDING_DISCLOSURE]: i18n.t('settings.ai.spotlights.recording_disclosure.description'),
    [AiInsightListItem_Type.CHURN_RISK]: i18n.t('prompt_library.prompt.spotlight.description.churn_risk'),
    [AiInsightListItem_Type.EXTRA_MILE]: i18n.t('settings.spotlights.extra_mile.description'),
    [AiInsightListItem_Type.SENTIMENT]: i18n.t('settings.spotlights.sentiment.description'),
    [AiInsightListItem_Type.OUTLIER]: i18n.t('settings.spotlights.outlier.description'),
    [AiInsightListItem_Type.FOLLOW_UP]: i18n.t('settings.spotlights.follow_up.description'),
    [AiInsightListItem_Type.ESCALATION_REQUEST]: i18n.t('settings.spotlights.escalation_request.description'),
    [AiInsightListItem_Type.SLA]: i18n.t('settings.spotlights.sla.description'),
    [AiInsightListItem_Type.BOT_REPETITION]: i18n.t('filter.option.description.bot_repetition'),
    [AiInsightListItem_Type.BOT_COMMUNICATION_EFFICIENCY]: i18n.t(
      'filter.option.description.bot_communication_efficiency',
    ),
  }
  const key = spotlightDescKeys[code.toUpperCase()]
  return key ?? ''
}

export function isSystemAutoQACategory(type: AutoQaCategoryType) {
  return type && type !== AutoQaCategoryType.Custom && type !== AutoQaCategoryType.Prompt
}

export function getCategoryName(category: Pick<Category, 'categoryName' | 'autoQaCategory'> | undefined): string {
  if (!category) return ''

  return isSystemAutoQACategory(category.autoQaCategory)
    ? translateSystemCategoryName(category.autoQaCategory)
    : category.categoryName || ''
}

export function getCategoryDescription(
  category: Pick<Category, 'categoryDescription' | 'autoQaCategory'> | undefined,
): string {
  if (!category) return ''

  return isSystemAutoQACategory(category.autoQaCategory)
    ? translateSystemCategoryDescription(category.autoQaCategory)
    : category.categoryDescription || ''
}

const metadataTranslations = {
  metadata_needed__channel: () => i18n.t('settings.ai.create.prompt.metadata_channel'),
  metadata_needed__internal_notes: () => i18n.t('settings.ai.create.prompt.metadata_internal_notes'),
  metadata_needed__tags: () => i18n.t('settings.ai.create.prompt.metadata_tags'),
  metadata_needed__time: () => i18n.t('settings.ai.create.prompt.metadata_time'),
} as const

export function getMetadataItems(metadata: Record<string, string>): string[] {
  return Object.entries(metadataTranslations)
    .filter(([key]) => metadata[key] === 'true')
    .map(([, translateFn]) => translateFn())
}
